
import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export function useVoiceRecording() {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const recordedAudioRef = useRef<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const onAudioReadyRef = useRef<(() => void) | null>(null);
  const pendingAutoSubmitRef = useRef<boolean>(false);

  const processVoiceInput = useCallback(async (audioBlob: Blob, currentLanguage: string) => {
    try {
      console.log("Processing voice input from blob:", audioBlob.type, audioBlob.size);
      setIsProcessingAudio(true);
      
      const base64Audio = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });

      console.log("Voice audio converted to base64, length:", base64Audio.length);

      // Mapping amélioré pour le créole haïtien
      const languageMapping: Record<string, string> = {
        'ht': 'ht', // Maintenant on envoie directement 'ht' pour le créole
        'fr': 'fr',
        'en': 'en',
        'es': 'es',
        'ar': 'ar'
      };

      const detectionLanguage = languageMapping[currentLanguage] || 'ht'; // Défaut vers créole

      console.log("Transcribing with language preference:", detectionLanguage);
      console.log("Original language setting:", currentLanguage);

      const { data: transcriptionData, error: transcriptionError } = await supabase.functions.invoke('transcribe-audio', {
        body: { 
          audio: base64Audio,
          language: detectionLanguage
        }
      });

      if (transcriptionError) {
        console.error("Transcription error:", transcriptionError);
        toast.error("Erreur lors de la transcription de l'audio");
        return null;
      }

      const transcribedText = transcriptionData?.text;
      console.log("Transcribed text:", transcribedText);
      console.log("Detected language:", transcriptionData?.language);
      console.log("Language code:", transcriptionData?.languageCode);
      
      // Affichage des détails d'analyse pour le créole haïtien
      if (transcriptionData?.haitianCreoleAnalysis) {
        console.log("Haitian Creole analysis:", transcriptionData.haitianCreoleAnalysis);
      }

      if (!transcribedText || transcribedText.trim() === '') {
        toast.error("Aucun texte détecté dans l'enregistrement vocal");
        return null;
      }

      // Messages de succès améliorés
      if (transcriptionData?.languageCode === 'ht') {
        toast.success(`Transcription kreyòl ayisyen réussie`);
      } else if (transcriptionData?.language && transcriptionData.language !== 'Unknown') {
        toast.success(`Transcription réussie (${transcriptionData.language})`);
      } else {
        toast.success("Transcription réussie");
      }

      return transcribedText;
    } catch (error) {
      console.error("Error processing voice input:", error);
      toast.error("Erreur lors du traitement de l'enregistrement vocal");
      return null;
    } finally {
      setIsProcessingAudio(false);
    }
  }, []);

  const startRecording = async () => {
    try {
      console.log("Starting audio recording with proper format...");
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      const audioChunks: Blob[] = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        recordedAudioRef.current = audioBlob;
        console.log("Audio recording completed, blob size:", audioBlob.size);
        
        stream.getTracks().forEach(track => track.stop());
        
        // Vérifier s'il y a un envoi automatique en attente
        if (pendingAutoSubmitRef.current) {
          console.log("Auto-submit pending, triggering callback immediately");
          pendingAutoSubmitRef.current = false;
          
          // Déclencher immédiatement le callback
          setTimeout(() => {
            if (onAudioReadyRef.current) {
              onAudioReadyRef.current();
            }
          }, 100); // Court délai pour s'assurer que l'état est mis à jour
        }
        
        toast.success("Enregistrement vocal terminé");
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      
      toast.success("Enregistrement vocal démarré");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log("Stopping recording normally");
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const stopRecordingAndSubmit = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log("Stopping recording with auto-submit flag");
      pendingAutoSubmitRef.current = true;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const setOnAudioReady = useCallback((callback: (() => void) | null) => {
    onAudioReadyRef.current = callback;
  }, []);

  const saveVoiceRecording = useCallback((audioUrl: string) => {
    console.log("Voice recording saved (legacy):", audioUrl);
  }, []);

  return {
    isRecording,
    isProcessingAudio,
    recordedAudioRef,
    processVoiceInput,
    startRecording,
    stopRecording,
    stopRecordingAndSubmit,
    saveVoiceRecording,
    setOnAudioReady
  };
}
